import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Drawer, Table, Tabs, Typography } from 'antd';
import { getMemberCurrentPlanCourse } from '@/services/technical';
import CurrentPlanAnalysisPanel from './CurrentPlanAnalysisPanel';
import styles from './PlanPresentation.less';

export function readPlanCourse(response, query) {
  const data = response?.data;
  const text = (value, max, required = false) =>
    value == null
      ? !required
      : typeof value === 'string' &&
        value.length <= max &&
        (!required || !!value.trim());
  const number = (value) =>
    value == null || (Number.isSafeInteger(value) && value >= 0);
  if (
    !['00000', '0'].includes(String(response?.code)) ||
    String(data?.memberId) !== String(query.memberId) ||
    data?.currentPlanId !== query.currentPlanId ||
    data?.courseType !== query.courseType ||
    data?.courseIdent !== query.courseIdent ||
    !text(data?.name, 200, true) ||
    !Array.isArray(data?.phases) ||
    !data.phases.length ||
    data.phases.length > 50
  )
    throw new Error('Invalid course response');
  let count = 0;
  data.phases.forEach((phase, index) => {
    if (
      !phase ||
      phase.sequence !== index + 1 ||
      !text(phase.name, 100) ||
      !text(phase.method, 300) ||
      !number(phase.restSeconds) ||
      !Array.isArray(phase.actions) ||
      phase.actions.length > 500
    )
      throw new Error('Invalid phase');
    phase.actions.forEach((action, actionIndex) => {
      if (
        ++count > 500 ||
        !action ||
        action.sequence !== actionIndex + 1 ||
        !Number.isSafeInteger(action.actionId) ||
        action.actionId <= 0 ||
        !text(action.name, 200, true) ||
        !text(action.volume, 300) ||
        !number(action.restSeconds)
      )
        throw new Error('Invalid action');
    });
  });
  if (!count) throw new Error('Missing actions');
  return data;
}

export default function CurrentPlanCourse({
  memberId,
  currentPlanId,
  courseType,
  courseIdent,
  onClose,
}) {
  const identity = `${memberId}:${currentPlanId}:${courseType}:${courseIdent}`;
  const generation = useRef(0);
  const [revision, setRevision] = useState(0);
  const [state, setState] = useState({});
  useEffect(() => {
    const request = ++generation.current;
    setState({ identity, loading: true });
    const query = { memberId, currentPlanId, courseType, courseIdent };
    getMemberCurrentPlanCourse({ requestData: query })
      .then((response) => {
        const data = readPlanCourse(response, query);
        if (request === generation.current) setState({ identity, data });
      })
      .catch((error) => {
        if (request !== generation.current) return;
        const messages = {
          401: '请先登录后台',
          403: '无会员档案菜单权限',
          409: '计划或课程已变化，请关闭并刷新计划概览',
          422: '此课程类型暂不支持计划动作详情',
        };
        setState({
          identity,
          error:
            messages[error?.response?.status] ||
            '课程动作与参数暂时无法加载，请重试',
        });
      });
    return () => {
      generation.current += 1;
    };
  }, [identity, memberId, currentPlanId, courseType, courseIdent, revision]);
  const current = state.identity === identity ? state : {};
  return (
    <Drawer
      title="当前计划 · 课程详情"
      visible
      width="min(900px, 95vw)"
      onClose={onClose}
      className={styles.planCourseDrawer}
    >
      {current.data ? (
        <>
          <div className={styles.planCourseHero}>
            <span>计划课程</span>
            <h3>{current.data.name}</h3>
            <div>
              <strong>{current.data.phases.length}</strong> 个训练阶段 ·{' '}
              <strong>
                {current.data.phases.reduce(
                  (count, phase) => count + phase.actions.length,
                  0,
                )}
              </strong>{' '}
              个动作
            </div>
          </div>
          <Typography.Paragraph
            type="secondary"
            className={styles.planCourseHint}
          >
            展示当前计划采用的课程结构及教练调整结果，仅供查看，不代表本次上课的实际完成情况。
          </Typography.Paragraph>
          <Tabs defaultActiveKey="actions" className={styles.planCourseTabs}>
            <Tabs.TabPane tab="动作安排" key="actions">
              {current.data.phases.map((phase) => (
                <section
                  key={phase.sequence}
                  className={styles.planCoursePhase}
                >
                  <div className={styles.planCoursePhaseHeader}>
                    <span>{phase.sequence}</span>
                    <div>
                      <Typography.Paragraph strong>
                        {phase.name || '阶段名称未提供'}
                      </Typography.Paragraph>
                      <Typography.Text type="secondary">
                        {phase.actions.length} 个动作 · 组间休息{' '}
                        {phase.restSeconds == null
                          ? '未提供'
                          : `${phase.restSeconds} 秒`}
                      </Typography.Text>
                    </div>
                  </div>
                  {phase.method && (
                    <Typography.Paragraph className={styles.planCourseMethod}>
                      {phase.method}
                    </Typography.Paragraph>
                  )}
                  <Table
                    className={styles.planCourseActionTable}
                    size="small"
                    rowKey="sequence"
                    dataSource={phase.actions}
                    pagination={{ pageSize: 20, hideOnSinglePage: true }}
                    scroll={{ x: 600 }}
                    locale={{ emptyText: '本阶段未提供动作' }}
                    columns={[
                      { title: '顺序', dataIndex: 'sequence', width: 70 },
                      { title: '动作', dataIndex: 'name' },
                      {
                        title: '训练量',
                        dataIndex: 'volume',
                        render: (value) => value || '未提供',
                      },
                      {
                        title: '动作后休息',
                        dataIndex: 'restSeconds',
                        render: (value) =>
                          value == null ? '未提供' : `${value} 秒`,
                      },
                    ]}
                  />
                </section>
              ))}
            </Tabs.TabPane>
            <Tabs.TabPane tab="本节适配分析" key="analysis">
              <CurrentPlanAnalysisPanel
                key={identity}
                memberId={memberId}
                planId={currentPlanId}
                courseType={courseType}
                courseId={courseIdent}
              />
            </Tabs.TabPane>
          </Tabs>
        </>
      ) : current.error ? (
        <>
          <Typography.Paragraph type="warning">
            {current.error}
          </Typography.Paragraph>
          <Button onClick={() => setRevision((value) => value + 1)}>
            重试动作详情
          </Button>
        </>
      ) : (
        <div className={styles.planCourseLoading}>加载课程详情…</div>
      )}
    </Drawer>
  );
}
CurrentPlanCourse.propTypes = {
  memberId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  currentPlanId: PropTypes.number.isRequired,
  courseType: PropTypes.number.isRequired,
  courseIdent: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};
